// NPM packages
import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Typography, Button, Modal, Box, Grid } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import styled from '@emotion/styled';
import { observer } from 'mobx-react-lite';
// Core components
import { useServices } from 'services';
import HorizontallyCentered from 'components/core/HorizontallyCentered';
import CircularProgress from 'components/core/CircularProgress';
import H1 from 'components/core/H1';
// Hooks and types
import useLoadingStateManager from 'hooks/useLoadingStateManager';
import useUserApproval from 'hooks/useUserApproval';
import { LoadingStatus } from 'types/LoadingStateManager';
import User from 'types/User';

// Styled components
const Root = styled.div({});
const StyledAlert = styled(Alert)({
  marginBottom: '1rem',
});
const StyledH1 = styled(H1)(({ theme }) => ({
  color: theme?.palette?.primary?.main || '#1976d2',
  marginBottom: 0,
  textAlign: 'center',
  paddingTop: '16px',
}));
const CenteredContainer = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: '1.5rem',
});

// Props interface for the component
export interface ReviewNewUserProps {
  className?: string;
}

const ReviewNewUser: React.FC<ReviewNewUserProps> = ({ className }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { authService } = useServices();
  const loadingStateManager = useLoadingStateManager();

  // Use custom hook for user approval logic
  const {
    pendingUsers,
    recentlyReviewedUsers,
    isLoading,
    fetchUsers,
    approveUser,
    denyUser,
  } = useUserApproval();

  const approvalLoading = loadingStateManager.loadingState.status === LoadingStatus.LOADING || isLoading;
  const currentUser = authService.user;

  // Fetch users on component mount
  useEffect((): void => {
    loadingStateManager.startLoading({
      waitOn: fetchUsers(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApprove = (user: User): void => {
    approveUser(user);
  };

  const handleDeny = (user: User): void => {
    denyUser(user);
  };

  const handleRowClick = (user: User): void => {
    if (user) {
      setSelectedUser(user);
      setModalOpen(true);
    }
  };

  const handleCloseModal = (): void => {
    setModalOpen(false);
    setSelectedUser(null);
  };

  // Render the user modal
  const renderUserModal = (): React.ReactElement | null => {
    if (!selectedUser) return null;

    return (
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="user-modal-title"
        aria-describedby="user-modal-desc"
      >
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: 4,
          backgroundColor: 'white',
          borderRadius: 2,
          maxWidth: 600,
          margin: 'auto',
          overflow: 'hidden',
          wordWrap: 'break-word',
        }}
      >
        <H1>Review User Information</H1>
        <Grid container spacing={3} sx={{ marginBottom: '1.5rem' }}>
          <Grid item xs={12}>
            <Typography>
              The below user was recently created. Please verify they have a legitimate interest in using MSD-LIVE.
            </Typography>
          </Grid>
          {selectedUser.approved !== null && (
            <Grid item xs={12}>
              <Alert severity={selectedUser.approved ? 'info' : 'warning'}>
                <AlertTitle>
                  This user has been {selectedUser.approved ? 'APPROVED' : 'DENIED'}
                </AlertTitle>
              </Alert>
            </Grid>
          )}

          <Grid item xs={6}>
            <Typography variant="h3" component="h2">
              Name
            </Typography>
            <Typography sx={{ wordWrap: 'break-word' }}>
              {selectedUser.firstName} {selectedUser.lastName}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="h3" component="h2">
              Email
            </Typography>
            <Typography sx={{ wordWrap: 'break-word' }}>{selectedUser.email}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h3" component="h2">
              Affiliation
            </Typography>
            <Typography sx={{ wordWrap: 'break-word' }}>{selectedUser.affiliation}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h3" component="h2">
              Justification
            </Typography>
            <Typography sx={{ wordWrap: 'break-word' }}>{selectedUser.justification || 'Not provided'}</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h3" component="h2">
              Request Date
            </Typography>
            <Typography>{selectedUser.createdAt || 'Unknown'}</Typography>
          </Grid>
        </Grid>
        <Button variant="contained" onClick={handleCloseModal} sx={{ marginTop: 2 }}>
          Close
        </Button>
      </Box>
      </Modal>
    );
  };

  // Render main content of the page
  const renderReviewUsersContent = (): React.ReactElement => {
    if (!currentUser) {
      return (
        <CenteredContainer>
          <StyledAlert severity="error">
            <AlertTitle>Access Denied</AlertTitle>
            You must be logged in to use this feature.
          </StyledAlert>
        </CenteredContainer>
      );
    }

    if (!currentUser.isNewUserApprover) {
      return (
        <CenteredContainer>
          <StyledAlert severity="error">
            <AlertTitle>Access Denied</AlertTitle>
            You do not have access to this feature.
          </StyledAlert>
        </CenteredContainer>
      );
    }

    if (loadingStateManager.loadingState.status === LoadingStatus.LOADING
      )
    {
      return (
        <HorizontallyCentered>
          <CircularProgress text="Loading..." />
        </HorizontallyCentered>
      );
    }

    return (
      <>
      {pendingUsers.length === 0 ?
      (<CenteredContainer>
        <StyledAlert severity="info">
          <AlertTitle>No New User Requests Found</AlertTitle>
          There are no pending new user requests at this time.
        </StyledAlert>
      </CenteredContainer>) : (
      <CenteredContainer>
        <StyledH1>Review New Users</StyledH1>
        <Typography whiteSpace="nowrap" sx={{ marginBottom: 2 }}>
          The below users were recently created. Please verify they have a legitimate interest in using MSD-LIVE.
        </Typography>
        <UsersTable
          currentUser={currentUser}
          users={pendingUsers}
          approvalLoading={approvalLoading}
          onApprove={handleApprove}
          onDeny={handleDeny}
          onRowClick={handleRowClick}
        />
        {recentlyReviewedUsers.length > 0 && <Box sx={{ height: '32px' }} />}
        {renderUserModal()}
      </CenteredContainer>)}
      <CenteredContainer>
         <StyledH1>Recent User Requests</StyledH1>
        <UsersTable
          currentUser={currentUser}
          users={recentlyReviewedUsers}
          approvalLoading={approvalLoading}
          onApprove={handleApprove}
          onDeny={handleDeny}
          onRowClick={handleRowClick}
          showApprovalStatus={true}
        />
        {renderUserModal()}
      </CenteredContainer>
      </>
    );
  };

  return (
    <Root className={className}>
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <div style={{ maxWidth: '2500px', width: '100%', padding: '0 32px' }}>
          {renderReviewUsersContent()}
        </div>
      </div>
    </Root>
  );
};

// UsersTable component props
interface UsersTableProps {
  currentUser: User;
  users: User[];
  approvalLoading?: boolean;
  onApprove?: (user: User) => void;
  onDeny?: (user: User) => void;
  onRowClick: (user: User) => void;
  showApprovalStatus?: boolean;
}

// UsersTable component
const UsersTable: React.FC<UsersTableProps> = ({
  currentUser,
  users,
  approvalLoading = false,
  onApprove,
  onDeny,
  onRowClick,
  showApprovalStatus = false,
}): React.ReactElement => {
  const columns: GridColDef[] = [
    { field: 'firstName', headerName: 'First Name', flex: 0.7, minWidth: 120, align: 'left', headerAlign: 'left' },
    { field: 'lastName', headerName: 'Last Name', flex: 0.7, minWidth: 120, align: 'left', headerAlign: 'left' },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 180, align: 'left', headerAlign: 'left' },
    { field: 'affiliation', headerName: 'Affiliation', flex: 1, minWidth: 140, align: 'left', headerAlign: 'left' },
    { field: 'justification', headerName: 'Justification', flex: 1.2, minWidth: 180, align: 'left', headerAlign: 'left' },
    { field: 'createdAt', headerName: 'Request Date', flex: 0.7, minWidth: 120, align: 'left', headerAlign: 'left' },
  ];

  if (showApprovalStatus) {
    columns.push({
      field: 'approved',
      headerName: 'Approval Status',
      flex: 0.7,
      minWidth: 120,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography
          sx={{
            color: params.row.approved ? 'green' : 'red',
            fontWeight: 'bold',
          }}
        >
          {params.row.approved ? 'Approved' : 'Denied'}
        </Typography>
      ),
    });
  } else {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 280,
      sortable: false,
      filterable: false,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => {
        const user = params.row as User;

        return (
          <Box sx={{
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-start',
            width: '100%',
            minWidth: 280,
            // flexWrap: 'nowrap',
            alignItems: 'center'
          }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={approvalLoading}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                onApprove?.(user);
              }}
              sx={{
                minWidth: 80,
                flexShrink: 0,
                fontSize: '0.875rem',
                padding: '6px 12px'
              }}
            >
              Approve
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={approvalLoading}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                onDeny?.(user);
              }}
              sx={{
                minWidth: 80,
                flexShrink: 0,
                fontSize: '0.875rem',
                padding: '6px 12px'
              }}
            >
              Deny
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              sx={{
                minWidth: 80,
                flexShrink: 0,
                fontSize: '0.875rem',
                padding: '6px 12px'
              }}
              component="a"
              href={`mailto:${user.email}?subject=Regarding your MSD-LIVE account&body=${encodeURIComponent(
                `Hi ${user.firstName},\nYou recently requested a user account on MSD-LIVE. Please provide a more detailed justification as to why you need access to MSD-LIVE.\nBest,\n${(currentUser?.firstName || '').trim()}`.trim()
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event: React.MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
            >
              Contact
            </Button>
          </Box>
        );
      },
    });
  }

  return (
    <div style={{ width: '100%', minWidth: 960, maxWidth: '100vw', overflowX: 'auto' }}>
      <DataGrid
        autoHeight
        disableRowSelectionOnClick
        hideFooter
        rows={users}
        columns={columns}
        getRowId={(user) => user.email}
        onRowClick={(params) => onRowClick(params.row as User)}
        sx={{
          minWidth: 960,
          background: '#fff',
          borderRadius: 2,
          boxShadow: 1,
          mt: 2,
          width: '100%',
          cursor: 'pointer',
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            textAlign: 'left',
            minHeight: '52px',
            padding: '8px 16px',
          },
          '& .MuiDataGrid-columnHeader': {
            textAlign: 'left',
          },
          '& .MuiDataGrid-sortIcon': {
            opacity: 1,
            visibility: 'visible',
          },
          '& .MuiDataGrid-menuIcon': {
            display: 'none',
          },
        }}
        aria-label="Users Table"
      />
    </div>
  );
};

export default observer(ReviewNewUser);